import * as RadixSelect from '@radix-ui/react-select';

import { cn } from './cn.js';
import { Icon } from './Icon.js';

export interface SelectOption {
  value: string;
  /** Already translated by the caller. */
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  /** The chosen option's `value`; `""` shows the placeholder (or an option whose value is `""`). */
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  /**
   * Pairs the dropdown with its `<label htmlFor>`. The trigger is a `<button>`, so without it a
   * click on the label would not reach the dropdown and screen readers would not read the label.
   */
  id?: string;
  /**
   * Shown while no option is chosen. Already translated: `packages/ui` has no `@mypoz/i18n`
   * dependency and never calls `t()` (see `Sheet`'s `closeLabel`).
   */
  placeholder?: string;
  disabled?: boolean;
  /**
   * Marks the field as required for screen readers (`aria-required`). Not Radix's own `required`:
   * that adds a hidden native <select> the browser validates, which blocks the form with a
   * "not focusable" error instead of a message. The form decides when it can be sent, as
   * PackageFormSheet's Save does (MYP-129).
   */
  required?: boolean;
  /** Extra classes on the closed control — a width, mostly (`w-20`, `sm:w-56`). */
  className?: string;
  'aria-label'?: string;
}

/*
  The closed control looks like the native <select> it replaces: the same classes as `selectClass`
  in apps/web/src/lib/styles.ts (height, padding, corners, hover, the brand focus outline). Copied
  rather than imported, since this package cannot depend on the app; keep the two in step.
*/
const TRIGGER = cn(
  'flex min-h-11 w-full items-center justify-between gap-2 rounded-md border border-line bg-surface py-2 pr-2.5 pl-3',
  'text-left text-sm text-ink transition-colors hover:bg-surface-alt lg:min-h-0',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface',
  'data-placeholder:text-muted',
);

/*
  Radix keeps "" for "nothing chosen" and refuses it as an item value. Callers use "" for a real
  choice too ("All", or a placeholder value), so it is swapped for a private value on the way in and
  back on the way out. Written as the \u0000 escape: a raw NUL byte makes git treat the file as
  binary.
*/
const EMPTY = '\u0000empty';
const toRadix = (value: string) => (value === '' ? EMPTY : value);
const fromRadix = (value: string) => (value === EMPTY ? '' : value);

/**
 * A dropdown in the app's own style, in place of the browser's native <select> (MYP-137). Built on
 * Radix Select, ported from the OTC component library, so the keyboard works as it does on a
 * native one: Enter, Space or the arrows open it, the arrows and typing move, Enter picks, Escape
 * closes and keeps focus on the control.
 *
 * The list opens under the control at the control's width, never taller than the screen allows;
 * a long list scrolls. On a phone each row is 44px tall.
 */
export function Select({
  value,
  onChange,
  options,
  id,
  placeholder,
  disabled,
  required,
  className,
  'aria-label': ariaLabel,
}: SelectProps) {
  const hasEmptyOption = options.some((option) => option.value === '');

  return (
    <RadixSelect.Root
      // With no "" option, "" means nothing is chosen: Radix shows the placeholder for "".
      value={value === '' && !hasEmptyOption ? '' : toRadix(value)}
      onValueChange={(next) => onChange(fromRadix(next))}
      disabled={disabled}
    >
      <RadixSelect.Trigger
        id={id}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        className={cn(TRIGGER, className)}
      >
        <span className="min-w-0 truncate">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon className="flex shrink-0 text-muted">
          <Icon name="expand_more" size={20} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          // Above Sheet and Modal (z-50), the same layer as the tour (z-60).
          className={cn(
            'z-60 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden',
            'max-w-[calc(100vw-1rem)] rounded-md border border-line bg-surface shadow-raised',
          )}
        >
          <RadixSelect.ScrollUpButton className="flex h-6 items-center justify-center text-muted">
            <Icon name="keyboard_arrow_up" size={18} />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={toRadix(option.value)}
                disabled={option.disabled}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-r-sm border-l-[3px] border-transparent py-2 pr-3 pl-2.5 lg:min-h-0',
                  'text-sm text-ink outline-none select-none',
                  // The row under the pointer or the arrow keys: a light red tint and a red bar on
                  // the left, like the current page in the profile menu (AccountMenu).
                  'data-highlighted:border-brand data-highlighted:bg-brand/10 data-highlighted:text-brand-dark',
                  'data-[state=checked]:font-semibold',
                  'data-disabled:cursor-not-allowed data-disabled:opacity-50',
                )}
              >
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="shrink-0">
                  <Icon name="check" size={18} className="text-brand" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="flex h-6 items-center justify-center text-muted">
            <Icon name="keyboard_arrow_down" size={18} />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
